import {
    buildPpeGroups,
    isPpeDepreciationName,
} from '@/components/account/gl-invoice/mode/tabs/ppeColumnUtils';
import {sumByKeys} from '@/components/account/gl-invoice/mode/tabs/bankColumnUtils';
import {buildColumnKey, type GlColumnDef} from '@/services/accountService/glInvoiceService';
import type {PPERow} from '@/services/auditService/interface/wp-row';

export type PpeGlPrefillSection =
    | 'locationOfProperty'
    | 'commercialAllowance'
    | 'industrialAllowance'
    | 'furnitureFixture'
    | 'computerHardSoft'
    | 'fullPayMotor'
    | 'hirePurchaseMotor'
    | 'machinery'
    | 'liftEscalator'
    | 'equipment';

export type PpeGlPrefillResult = Partial<Record<PpeGlPrefillSection, PPERow[]>>;

type SheetLike = {
    columnDefs: GlColumnDef[];
    summaryCurrentPeriod: Record<string, number>;
    summaryPriorYear: Record<string, number>;
};

/** 将 GL PPE 分组标签映射到 F1 子表 section */
export function mapPpeGroupToSection(label: string, groupKey: string): PpeGlPrefillSection | null {
    const s = `${label} ${groupKey}`.toLowerCase();
    if (/(hire\s*purchase|hp\s*motor)/.test(s)) return 'hirePurchaseMotor';
    if (/(motor|vehicle)/.test(s)) return 'fullPayMotor';
    if (/(leasehold|improvement|renovation|fit[\s-]?out)/.test(s)) return 'commercialAllowance';
    if (/industrial/.test(s)) return 'industrialAllowance';
    if (/(furniture|fixture)/.test(s)) return 'furnitureFixture';
    if (/(computer|software|hardware)/.test(s)) return 'computerHardSoft';
    if (/(plant|machiner)/.test(s)) return 'machinery';
    if (/(lift|escalator)/.test(s)) return 'liftEscalator';
    if (/(land|building|property)/.test(s)) return 'locationOfProperty';
    if (/equipment/.test(s)) return 'equipment';
    return null;
}

function round2(n: number): number {
    return Math.round(n * 100) / 100;
}

/**
 * 从 GL Sheet 汇总生成 F1 各子表种子行（Cost b/f / Additions / Acc dep b/f / Depn override）。
 * summaryCurrentPeriod 按当期变动（movement），summaryPriorYear 按 Row10。
 */
export function buildPpeGlPrefillFromSheet(sheet: SheetLike | null | undefined): PpeGlPrefillResult {
    if (!sheet?.columnDefs?.length) return {};

    const groups = buildPpeGroups(sheet.columnDefs);
    const result: PpeGlPrefillResult = {};
    let tempId = -1;

    for (const group of groups) {
        const section = mapPpeGroupToSection(group.groupLabel, group.groupKey);
        if (!section) continue;

        const costKeys = group.columns
            .filter((c) => !isPpeDepreciationName(c.nameEn))
            .map((c) => buildColumnKey(c));
        const depKeys = group.columns
            .filter((c) => isPpeDepreciationName(c.nameEn))
            .map((c) => buildColumnKey(c));

        const costPrior = sumByKeys(sheet.summaryPriorYear, costKeys);
        const costMovement = sumByKeys(sheet.summaryCurrentPeriod, costKeys);
        const depPrior = sumByKeys(sheet.summaryPriorYear, depKeys);
        const depMovement = sumByKeys(sheet.summaryCurrentPeriod, depKeys);

        const costBf = round2(costPrior);
        const additions = costMovement > 0.005 ? round2(costMovement) : null;
        const disposalCost = costMovement < -0.005 ? round2(costMovement) : null;
        const accDepBf = round2(Math.abs(depPrior));
        // Acc dep 增加（GL 常为负向变动）→ 折旧费用
        const depnCharge = round2(Math.abs(depMovement) > 0.005 ? Math.abs(depMovement) : 0);

        if (
            Math.abs(costBf) < 0.005
            && (additions == null || Math.abs(additions) < 0.005)
            && (disposalCost == null || Math.abs(disposalCost) < 0.005)
            && Math.abs(accDepBf) < 0.005
            && Math.abs(depnCharge) < 0.005
        ) {
            continue;
        }

        const row: PPERow = {
            id: tempId--,
            date: null,
            item: group.groupLabel,
            costBf: Math.abs(costBf) > 0.005 ? costBf : null,
            additions,
            disposalCost,
            accDepBf: Math.abs(accDepBf) > 0.005 ? accDepBf : null,
            ...(Math.abs(depnCharge) > 0.005 ? {depnChgChange: depnCharge} : {}),
        };

        const list = result[section] ?? [];
        list.push(row);
        result[section] = list;
    }

    return result;
}

export function isPpeRowsBlank(rows: PPERow[] | null | undefined): boolean {
    if (!rows || rows.length === 0) return true;
    return rows.every((row) => {
        const hasItem = String(row.item || '').trim() !== '';
        const hasCost = row.costBf != null && Number(row.costBf) !== 0;
        const hasAdd = row.additions != null && Number(row.additions) !== 0;
        const hasDep = row.accDepBf != null && Number(row.accDepBf) !== 0;
        const hasSavedId = row.id != null && Number(row.id) > 0;
        return !hasSavedId && (!hasItem || (!hasCost && !hasAdd && !hasDep));
    });
}
